<div class="login-container">
  <!-- Background with gradient overlay -->
  <div class="login-background">
    <div class="background-overlay"></div>
  </div>

  <!-- Main forgot password card -->
  <div class="login-card">
    <!-- Header section -->
    <div class="login-header">
      <div class="logo-container">
        <!-- Placeholder for company logo -->
        <div class="logo-placeholder">
          <img src="assets/portal/images/stem/logo-white.png" alt="STEM Logo" class="stem-logo" />
        </div>
      </div>
      <!-- Dynamic title based on state -->
      <h1 class="login-title">
        <ng-container *ngIf="state === 'forgotPassword'">Forgot Password</ng-container>
        <ng-container *ngIf="state === 'setPassword'">Set Password</ng-container>
        <ng-container *ngIf="state === 'resetPassword'">Reset Password</ng-container>
        <ng-container *ngIf="state === 'requestSent'">Check Your Email</ng-container>
        <ng-container *ngIf="state === 'resetSent'">Password Reset</ng-container>
      </h1>

      <p class="login-subtitle">
        <ng-container *ngIf="state === 'forgotPassword'"
          >Enter your email to reset your password</ng-container
        >
        <ng-container *ngIf="state === 'setPassword'">Please set your new password</ng-container>
        <ng-container *ngIf="state === 'resetPassword'">Enter your new password</ng-container>
        <ng-container *ngIf="state === 'requestSent'"
          >We've sent a reset link to your email</ng-container
        >
        <ng-container *ngIf="state === 'resetSent'"
          >Your password has been successfully reset</ng-container
        >
      </p>
    </div>

    <!-- Form container -->
    <div class="login-form-container">
      <!-- Forgot Password Form -->
      <div *ngIf="state === 'forgotPassword'">
        <form [formGroup]="forgotPasswordForm" (ngSubmit)="onForgotSubmit()" class="login-form">
          <!-- Email field -->
          <div class="form-field">
            <label for="email" class="field-label">
              <i class="pi pi-envelope field-icon"></i>
              Email Address
            </label>
            <div class="input-container">
              <input
                id="email"
                type="email"
                formControlName="email"
                class="form-input"
                [class.input-error]="isFieldInvalid('email')"
                placeholder="Enter your email address"
                autocomplete="email"
                (input)="clearError()"
                [attr.aria-describedby]="isFieldInvalid('email') ? 'email-error' : null"
                [attr.aria-invalid]="isFieldInvalid('email')"
              />
              <i class="pi pi-envelope input-icon"></i>
            </div>
            <div
              *ngIf="isFieldInvalid('email')"
              class="error-message"
              id="email-error"
              role="alert"
            >
              <i class="pi pi-exclamation-triangle"></i>
              <span>{{ getFieldError('email') }}</span>
            </div>
          </div>

          <!-- Submit button -->
          <button
            type="submit"
            class="login-button"
            [disabled]="loading || forgotPasswordForm.invalid"
            [class.loading]="loading"
          >
            <span *ngIf="!loading" class="button-content">
              <i class="pi pi-send"></i>
              Send Reset Link
            </span>
            <span *ngIf="loading" class="button-content">
              <i class="pi pi-spin pi-spinner"></i>
              Sending...
            </span>
          </button>

          <!-- Error message -->
          <div *ngIf="error" class="error-alert" role="alert" aria-live="polite">
            <i class="pi pi-exclamation-circle"></i>
            <span>{{ error }}</span>
          </div>
        </form>

        <!-- Back to login link -->
        <div class="back-to-login">
          <a class="back-link" [routerLink]="['/login']" tabindex="0">
            <i class="pi pi-arrow-left"></i>
            Back to Login
          </a>
        </div>
      </div>

      <!-- Set Password Form -->
      <div *ngIf="state === 'setPassword'">
        <form [formGroup]="newPasswordForm" (ngSubmit)="onResetSubmit()" class="login-form">
          <!-- Password field -->
          <div class="form-field">
            <label for="password" class="field-label">
              <i class="pi pi-lock field-icon"></i>
              New Password
            </label>
            <div class="input-container">
              <input
                id="password"
                [type]="showPassword ? 'text' : 'password'"
                formControlName="password"
                class="form-input"
                [class.input-error]="isFieldInvalid('password')"
                placeholder="Enter your new password"
                autocomplete="new-password"
                (input)="clearError()"
                [attr.aria-describedby]="isFieldInvalid('password') ? 'password-error' : null"
                [attr.aria-invalid]="isFieldInvalid('password')"
              />
              <i class="pi pi-lock input-icon"></i>
              <button
                type="button"
                class="password-toggle"
                (click)="togglePasswordVisibility()"
                [attr.aria-label]="showPassword ? 'Hide password' : 'Show password'"
              >
                <i [class]="showPassword ? 'pi pi-eye-slash' : 'pi pi-eye'"></i>
              </button>
            </div>
            <div
              *ngIf="isFieldInvalid('password')"
              class="error-message"
              id="password-error"
              role="alert"
            >
              <i class="pi pi-exclamation-triangle"></i>
              <span>{{ getFieldError('password') }}</span>
            </div>
          </div>

          <!-- Confirm Password field -->
          <div class="form-field">
            <label for="confirmPassword" class="field-label">
              <i class="pi pi-lock field-icon"></i>
              Confirm Password
            </label>
            <div class="input-container">
              <input
                id="confirmPassword"
                [type]="showConfirmPassword ? 'text' : 'password'"
                formControlName="confirmPassword"
                class="form-input"
                [class.input-error]="isFieldInvalid('confirmPassword')"
                placeholder="Confirm your new password"
                autocomplete="new-password"
                (input)="clearError()"
                [attr.aria-describedby]="
                  isFieldInvalid('confirmPassword') ? 'confirmPassword-error' : null
                "
                [attr.aria-invalid]="isFieldInvalid('confirmPassword')"
              />
              <i class="pi pi-lock input-icon"></i>
              <button
                type="button"
                class="password-toggle"
                (click)="toggleConfirmPasswordVisibility()"
                [attr.aria-label]="showConfirmPassword ? 'Hide password' : 'Show password'"
              >
                <i [class]="showConfirmPassword ? 'pi pi-eye-slash' : 'pi pi-eye'"></i>
              </button>
            </div>
            <div
              *ngIf="isFieldInvalid('confirmPassword')"
              class="error-message"
              id="confirmPassword-error"
              role="alert"
            >
              <i class="pi pi-exclamation-triangle"></i>
              <span>{{ getFieldError('confirmPassword') }}</span>
            </div>
          </div>

          <!-- Submit button -->
          <button
            type="submit"
            class="login-button"
            [disabled]="loading || newPasswordForm.invalid"
            [class.loading]="loading"
          >
            <span *ngIf="!loading" class="button-content">
              <i class="pi pi-check"></i>
              Set Password
            </span>
            <span *ngIf="loading" class="button-content">
              <i class="pi pi-spin pi-spinner"></i>
              Setting Password...
            </span>
          </button>

          <!-- Error message -->
          <div *ngIf="error" class="error-alert" role="alert" aria-live="polite">
            <i class="pi pi-exclamation-circle"></i>
            <span>{{ error }}</span>
          </div>
        </form>
      </div>

      <!-- Reset Password Form -->
      <div *ngIf="state === 'resetPassword'">
        <form [formGroup]="newPasswordForm" (ngSubmit)="onResetSubmit()" class="login-form">
          <!-- Password field -->
          <div class="form-field">
            <label for="password" class="field-label">
              <i class="pi pi-lock field-icon"></i>
              New Password
            </label>
            <div class="input-container">
              <input
                id="password"
                [type]="showPassword ? 'text' : 'password'"
                formControlName="password"
                class="form-input"
                [class.input-error]="isFieldInvalid('password')"
                placeholder="Enter your new password"
                autocomplete="new-password"
                (input)="clearError()"
                [attr.aria-describedby]="isFieldInvalid('password') ? 'password-error' : null"
                [attr.aria-invalid]="isFieldInvalid('password')"
              />
              <i class="pi pi-lock input-icon"></i>
              <button
                type="button"
                class="password-toggle"
                (click)="togglePasswordVisibility()"
                [attr.aria-label]="showPassword ? 'Hide password' : 'Show password'"
              >
                <i [class]="showPassword ? 'pi pi-eye-slash' : 'pi pi-eye'"></i>
              </button>
            </div>
            <div
              *ngIf="isFieldInvalid('password')"
              class="error-message"
              id="password-error"
              role="alert"
            >
              <i class="pi pi-exclamation-triangle"></i>
              <span>{{ getFieldError('password') }}</span>
            </div>
          </div>

          <!-- Confirm Password field -->
          <div class="form-field">
            <label for="confirmPassword" class="field-label">
              <i class="pi pi-lock field-icon"></i>
              Confirm Password
            </label>
            <div class="input-container">
              <input
                id="confirmPassword"
                [type]="showConfirmPassword ? 'text' : 'password'"
                formControlName="confirmPassword"
                class="form-input"
                [class.input-error]="isFieldInvalid('confirmPassword')"
                placeholder="Confirm your new password"
                autocomplete="new-password"
                (input)="clearError()"
                [attr.aria-describedby]="
                  isFieldInvalid('confirmPassword') ? 'confirmPassword-error' : null
                "
                [attr.aria-invalid]="isFieldInvalid('confirmPassword')"
              />
              <i class="pi pi-lock input-icon"></i>
              <button
                type="button"
                class="password-toggle"
                (click)="toggleConfirmPasswordVisibility()"
                [attr.aria-label]="showConfirmPassword ? 'Hide password' : 'Show password'"
              >
                <i [class]="showConfirmPassword ? 'pi pi-eye-slash' : 'pi pi-eye'"></i>
              </button>
            </div>
            <div
              *ngIf="isFieldInvalid('confirmPassword')"
              class="error-message"
              id="confirmPassword-error"
              role="alert"
            >
              <i class="pi pi-exclamation-triangle"></i>
              <span>{{ getFieldError('confirmPassword') }}</span>
            </div>
          </div>

          <!-- Submit button -->
          <button
            type="submit"
            class="login-button"
            [disabled]="loading || newPasswordForm.invalid"
            [class.loading]="loading"
          >
            <span *ngIf="!loading" class="button-content">
              <i class="pi pi-check"></i>
              Reset Password
            </span>
            <span *ngIf="loading" class="button-content">
              <i class="pi pi-spin pi-spinner"></i>
              Resetting Password...
            </span>
          </button>

          <!-- Error message -->
          <div *ngIf="error" class="error-alert" role="alert" aria-live="polite">
            <i class="pi pi-exclamation-circle"></i>
            <span>{{ error }}</span>
          </div>
        </form>
      </div>

      <!-- Success Messages -->
      <div *ngIf="state === 'requestSent'" class="success-message">
        <div class="success-icon">
          <i class="pi pi-check-circle"></i>
        </div>
        <h2 class="success-title">Check Your Email</h2>
        <p class="success-text">
          We've sent a password reset link to your email address. Please check your inbox and follow
          the instructions to reset your password.
        </p>
        <div class="back-to-login">
          <a class="back-link" [routerLink]="['/login']" tabindex="0">
            <i class="pi pi-arrow-left"></i>
            Back to Login
          </a>
        </div>
      </div>

      <div *ngIf="state === 'resetSent'" class="success-message">
        <div class="success-icon">
          <i class="pi pi-check-circle"></i>
        </div>
        <h2 class="success-title">Password Reset Successful</h2>
        <p class="success-text">
          Your password has been successfully reset. You can now log in with your new password.
        </p>
        <div class="back-to-login">
          <a class="back-link" [routerLink]="['/login']" tabindex="0">
            <i class="pi pi-sign-in"></i>
            Go to Login
          </a>
        </div>
      </div>
    </div>
  </div>
</div>
